import React, { useEffect, useState } from "react";

import { RegresoOpcion, RegresoProgramadoDTO, SugerenciaHorario, SugerenciasDTO, fechaLarga, hora12, regresaEl, sugerenciasApi } from "./sugerenciasApi";
import { tituloSugerencia } from "./sugerenciasHorario";
import "./sugerenciasHorario.css";

/*
	Regreso a clases con fecha (Actualizaciones v2, fase E). Cuando el horario elegido vuelve a dar clase en más de una semana:
	«Pausar Hasta [fecha]», «Clases de Cortesía en [horario] Hasta [fecha]» o «Mover Ya Sin Pausa». Las mensualidades no
	cursadas se corren en el servidor para que la primera venza el día del regreso.
*/

export type EleccionRegreso = { tipo: "pausa" } | { tipo: "cortesia"; horarioId: number | null } | { tipo: "ya" };

const UPDATE = { SCHEDULE_CHANGE: 1, REENTRY: 6, PAUSED: 11 };

/** ¿La elección está completa? (Cortesía pide su horario.) null = falta elegir. */
export const regresoListo = (e: EleccionRegreso | null): boolean => !!e && (e.tipo !== "cortesia" || !!e.horarioId);

/**
	Cómo se manda la actualización según la elección: Pausar Hasta desde una activa va como Pausar con el destino guardado; desde
	una flotante, como Reingreso que el sistema hace el día del regreso. Cortesía: el mismo tipo, con su horario de cortesía.
*/
export const regresoParaEnviar = (e: EleccionRegreso | null, typeId: number, destinoId: number): { typeId: number; options?: { regreso: RegresoOpcion } } => {
	if (!e || e.tipo === "ya") { return { typeId }; }
	if (e.tipo === "pausa") {
		return typeId === UPDATE.REENTRY ? { typeId, options: { regreso: { mode: "pause" } } } : { typeId: UPDATE.PAUSED, options: { regreso: { mode: "pause", targetScheduleId: destinoId } } };
	}
	return { typeId, options: { regreso: { mode: "courtesy", courtesyScheduleId: e.horarioId || undefined } } };
};

export const RegresoClasesOpciones: React.FC<{ destino: SugerenciaHorario; datos: SugerenciasDTO | null; valor: EleccionRegreso | null; onCambio: (e: EleccionRegreso) => void; deshabilitado?: boolean }> =
	({ destino, datos, valor, onCambio, deshabilitado }) => {
	const fecha = fechaLarga(destino.nextClass);
	const cortesia = (datos?.courtesy || []).filter((s) => s.id !== destino.id && (!destino.nextClass || !s.nextClass || s.nextClass < destino.nextClass)).slice(0, 4);
	const nombre = "regreso" + destino.id;
	return (
		<div className="sgh-regreso" role="radiogroup" aria-label={regresaEl(destino)}>
			<h4>{regresaEl(destino)}</h4>
			<label className="sgh-radio">
				<input type="radio" name={nombre} disabled={deshabilitado} checked={valor?.tipo === "pausa"} onChange={() => onCambio({ tipo: "pausa" })} />
				<span><b>Pausar Hasta el {fecha}</b></span>
			</label>
			{cortesia.length > 0 ? (
				<>
					<label className="sgh-radio">
						<input type="radio" name={nombre} disabled={deshabilitado} checked={valor?.tipo === "cortesia"} onChange={() => onCambio({ tipo: "cortesia", horarioId: cortesia.length === 1 ? cortesia[0].id : null })} />
						<span><b>Clases de Cortesía Hasta el {fecha}</b></span>
					</label>
					{valor?.tipo === "cortesia" ? (
						<div className="sgh-sub" role="radiogroup">
							{cortesia.map((s) => (
								<label key={"ct" + s.id} className="sgh-radio">
									<input type="radio" name={nombre + "c"} disabled={deshabilitado} checked={valor.horarioId === s.id} onChange={() => onCambio({ tipo: "cortesia", horarioId: s.id })} />
									<span>{tituloSugerencia(s)}{s.periodName ? ", " + s.periodName : ""}<span className="sgh-sec">, {s.nextClass ? "Próxima Clase " + fechaLarga(s.nextClass) + (s.nextClassTime ? " " + hora12(s.nextClassTime) : "") : ""}{s.mentorName ? ", " + s.mentorName : ""}</span></span>
								</label>
							))}
						</div>
					) : null}
				</>
			) : null}
			<label className="sgh-radio">
				<input type="radio" name={nombre} disabled={deshabilitado} checked={valor?.tipo === "ya"} onChange={() => onCambio({ tipo: "ya" })} />
				<span><b>Mover Ya Sin Pausa</b></span>
			</label>
		</div>
	);
};

/** Línea de un regreso ya programado: «Pausado, Regresa el …» / «Clases de Cortesía en … Hasta el …». */
export const textoRegreso = (p: RegresoProgramadoDTO): string =>
	p.kind === "courtesy"
		? `Clases de Cortesía en ${p.courtesy ? tituloSugerencia({ name: p.courtesy.name }) : ""} Hasta el ${p.returnDateLabel}`
		: `Regresa el ${p.returnDateLabel}${p.returnTime ? ", " + p.returnTime : ""}`;

/** Regresos programados de una matrícula, con «Cancelar Regreso» (las mensualidades vuelven a como estaban). */
export const RegresoProgramado: React.FC<{ campusId: number; enrollmentId: number; puedeCancelar: boolean; notify?: (t: string, ok?: boolean) => void; onCambio?: () => void }> =
	({ campusId, enrollmentId, puedeCancelar, notify, onCambio }) => {
	const [planes, setPlanes] = useState<RegresoProgramadoDTO[]>([]);
	const [cancelando, setCancelando] = useState<RegresoProgramadoDTO | null>(null);
	const [nota, setNota] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	const [tick, setTick] = useState<number>(0);
	useEffect(() => {
		let vigente = true;
		sugerenciasApi.regresos(campusId, { enrollmentId }).then((r) => vigente && setPlanes((r.data || []).filter((p) => p.statusId === 0))).catch(() => vigente && setPlanes([]));
		return () => { vigente = false; };
	}, [campusId, enrollmentId, tick]);
	if (planes.length === 0) { return null; }
	const cancelar = () => {
		if (!cancelando || nota.trim().length < 3) { return; }
		setBusy(true);
		sugerenciasApi.cancelarRegreso(campusId, cancelando.id, nota.trim())
			.then(() => { notify?.("Regreso cancelado; las mensualidades quedaron como estaban."); setCancelando(null); setNota(""); setTick((t) => t + 1); onCambio?.(); })
			.catch((e) => notify?.(e?.response?.data?.message || e?.response?.data?.errors?.error?.[0] || "No se pudo cancelar el regreso.", false))
			.finally(() => setBusy(false));
	};
	return (
		<>
			{planes.map((p) => (
				<div key={"rp" + p.id} className="sgh-plan">
					<span>
						<b>{textoRegreso(p)}</b>
						{p.target ? <span className="sgh-sec"><br />{tituloSugerencia({ name: p.target.name })}{p.target.periodName ? ", " + p.target.periodName : ""}</span> : null}
						{p.firstDueDate ? <span className="sgh-sec"><br />Siguiente Mensualidad {fechaLarga(p.firstDueDate)}</span> : null}
						{p.lastError ? <span className="sgh-oro"><br />{p.lastError}</span> : null}
					</span>
					{puedeCancelar ? <button type="button" className="sgh-mas" onClick={() => setCancelando(p)}>Cancelar Regreso</button> : null}
				</div>
			))}
			{cancelando ? (
				<div className="wau-modal-fondo" role="dialog" aria-modal="true" onClick={() => !busy && setCancelando(null)}>
					<div className="wau-modal" onClick={(e) => e.stopPropagation()}>
						<h3>Cancelar Regreso</h3>
						<p style={{ marginTop: 0 }}>{textoRegreso(cancelando)}</p>
						<textarea className="wau-entrada" rows={2} placeholder="Motivo (obligatorio)" aria-label="Motivo" value={nota} onChange={(e) => setNota(e.target.value)} />
						<div className="wau-acciones">
							<button type="button" className="wau-btn s" disabled={busy} onClick={() => setCancelando(null)}>Volver</button>
							<button type="button" className="wau-btn p" disabled={busy || nota.trim().length < 3} onClick={cancelar}>Cancelar Regreso</button>
						</div>
					</div>
				</div>
			) : null}
		</>
	);
};

/** Estado de Cuenta: regresos programados de la alumna (solo lectura; sin permiso de Actualizaciones no sale nada). */
export const RegresosDeAlumna: React.FC<{ campusId: number; studentId: number }> = ({ campusId, studentId }) => {
	const [planes, setPlanes] = useState<RegresoProgramadoDTO[]>([]);
	useEffect(() => {
		let vigente = true;
		sugerenciasApi.regresos(campusId, { studentId }).then((r) => vigente && setPlanes((r.data || []).filter((p) => p.statusId === 0))).catch(() => vigente && setPlanes([]));
		return () => { vigente = false; };
	}, [campusId, studentId]);
	if (planes.length === 0) { return null; }
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 6, margin: "0 0 10px 0" }}>
			{planes.map((p) => (
				<div key={"ra" + p.id} className="sgh-plan">
					<span>
						<b>{textoRegreso(p)}</b>
						{p.target ? <span className="sgh-sec">, {tituloSugerencia({ name: p.target.name })}{p.target.periodName ? ", " + p.target.periodName : ""}</span> : null}
						{p.firstDueDate ? <span className="sgh-sec">, Siguiente Mensualidad {fechaLarga(p.firstDueDate)}</span> : null}
					</span>
				</div>
			))}
		</div>
	);
};
